/** Form/context MFA email helpers only — no Okta or AuthService imports (avoids bundle cycles). */

const coalesceTrimmed = (...candidates: Array<unknown>): string => {
  const first = candidates.find((c) => c != null && String(c).trim().length > 0)
  return first == null ? '' : String(first).trim()
}

const trimmedFormField = (v: unknown): string => (v == null ? '' : String(v).trim())

/** True when the string is suitable as an MFA destination email (not a bare Okta login id). */
export const isPlausibleMfaEnrollmentEmail = (s: string | undefined | null): boolean => {
  if (typeof s !== 'string' || s.trim().length === 0) return false
  return s.indexOf('@') > 0
}

/**
 * Email to use for MFA enrollment and the Additional Details step.
 * Form: `formData.email` then `formData.mfaEmail`.
 * Context: if `context.email` contains `@`, use it; if not (e.g. legacy Okta login id without `@`) but
 * `context.mfaEmail` contains `@`, prefer `mfaEmail`; otherwise first non-empty of the two.
 */
export const resolveMfaEmailFromFormAndContext = (
  formData: { email?: { value?: unknown }; mfaEmail?: { value?: unknown } },
  context?: { email?: string; mfaEmail?: string } | null,
): string => {
  const fromForm = coalesceTrimmed(trimmedFormField(formData?.email?.value), trimmedFormField(formData?.mfaEmail?.value))
  if (fromForm) return fromForm

  const ctxEmail = context?.email
  const ctxMfa = context?.mfaEmail
  if (isPlausibleMfaEnrollmentEmail(ctxEmail)) return String(ctxEmail).trim()
  if (isPlausibleMfaEnrollmentEmail(ctxMfa)) return String(ctxMfa).trim()
  return coalesceTrimmed(ctxEmail, ctxMfa)
}

/**
 * Email for `POST /okta/authn/factors` when `factorType` is `email` (`profile.email`).
 * Okta requires a real mailbox; never send an Okta login id (e.g. `mfa_*.gqa.eb.NAME`) in `profile.email`.
 * Reuses form/context resolution, then keeps only values that look like an email (contain `@` before domain).
 */
export const resolveMfaEmailForOktaEnrollProfile = (
  formData: { email?: { value?: unknown }; mfaEmail?: { value?: unknown } },
  context?: { email?: string; mfaEmail?: string } | null,
): string => {
  const candidate = resolveMfaEmailFromFormAndContext(formData, context)
  if (isPlausibleMfaEnrollmentEmail(candidate)) {
    return String(candidate).trim()
  }
  return ''
}
